import { useEffect } from "react";

/**
 * A simple hook to detect clicks outside of a specified element
 * @param ref The ref object attached to the element to monitor for outside clicks
 * @param callback The function to call when a click outside is detected
 * @param isActive Whether the hook should be active (e.g., only when a dropdown is open)
 */
export const useSimpleOutsideClick = (
  ref: React.RefObject<HTMLElement | null>,
  callback: () => void,
  isActive: boolean
) => {
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      // Check if the ref has a current value and that the clicked element is not a child of the ref
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callback();
      }
    }

    // Only attach listener if the component is active (e.g., dropdown is open)
    if (isActive) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Cleanup function to remove the event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, callback, isActive]);
};
